import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { examApi, gamificationApi } from '../api';

export interface ExamLaunchOptions {
  category?: string;
  pathwayId?: string;
}

interface Props {
  onClose: () => void;
  onOpenExam?: (options?: ExamLaunchOptions) => void;
  onOpenMBTI?: () => void;
  onOpenMissions?: () => void;
  onComposeNote?: () => void;
}

interface IntellectComponent {
  key: string;
  label: string;
  description: string;
  value: number;
  max: number;
  pct: number;
  remaining: number;
  color: string;
}

interface NextAction {
  key: string;
  title: string;
  description: string;
  cta: string;
  target: string;
  gain: number;
}

interface TopDomain {
  key: string;
  name: string;
  icon: string;
  color: string;
  correct: number;
  total: number;
  attempts: number;
  percent: number;
  mastery_score: number;
  has_data: boolean;
  level: string;
  rank: number;
  subdomains?: string[];
  subdomain_count?: number;
}

interface IntellectData {
  intellect_score: number;
  intellect: number;
  score_gap: number;
  components: IntellectComponent[];
  latest_mbti: null | {
    type: string;
    date_label?: string;
    description?: string;
  };
  recent_exams: Array<{
    id: number;
    category_name: string;
    category_icon: string;
    score: number;
    total_questions: number;
    score_percent: number;
    created_at: string;
  }>;
  exam_stats: {
    total_attempts: number;
    total_answered: number;
    total_correct: number;
    avg_percent: number;
    study_streak: number;
  };
  weekly_tasks: {
    completed: number;
    total: number;
    score: number;
  };
  top_domains: TopDomain[];
  next_actions: NextAction[];
}

interface ExamCategory {
  key: string;
  name: string;
  icon: string;
  color: string;
  question_count: number;
}

interface PracticePathway {
  id: string;
  name: string;
  icon: string;
  color: string;
  current_sequence: number;
  question_count: number;
  category_keys: string[];
}

const DEFAULT_INTELLECT_DOMAINS: TopDomain[] = [
  { key: 'finance', name: '金融经济', icon: '💰', color: '#f59e0b', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 1 },
  { key: 'business_management', name: '商业管理', icon: '📈', color: '#f97316', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 2 },
  { key: 'health', name: '健康养生', icon: '🌿', color: '#22c55e', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 3 },
  { key: 'life_skills', name: '生活技能', icon: '🧰', color: '#84cc16', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 4 },
  { key: 'history', name: '历史人文', icon: '🏛️', color: '#a78bfa', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 5 },
  { key: 'literature_art', name: '文学艺术', icon: '🎨', color: '#fb7185', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 6 },
  { key: 'philosophy_religion', name: '哲学宗教', icon: '🪷', color: '#c084fc', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 7 },
  { key: 'psychology', name: '心理认知', icon: '🧠', color: '#f472b6', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 8 },
  { key: 'communication_relationship', name: '沟通关系', icon: '💬', color: '#2dd4bf', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 9 },
  { key: 'logic_math', name: '逻辑数学', icon: '∑', color: '#0ea5e9', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 10 },
  { key: 'science', name: '科学科技', icon: '🔬', color: '#8b5cf6', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 11 },
  { key: 'ai_computing', name: 'AI与计算机', icon: '🤖', color: '#10b981', correct: 0, total: 0, attempts: 0, percent: 0, mastery_score: 0, has_data: false, level: '待探索', rank: 12 },
];

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    <path d="M3 21v-5h5" />
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M21 3v5h-5" />
  </svg>
);

const clampPct = (value: number) => Math.max(0, Math.min(100, Math.round(value || 0)));
const polarPoint = (index: number, total: number, radius: number) => {
  const angle = (-90 + (360 / Math.max(total, 1)) * index) * Math.PI / 180;
  return {
    x: 50 + Math.cos(angle) * radius,
    y: 50 + Math.sin(angle) * radius,
  };
};

const domainValue = (domain: TopDomain) => {
  if (!domain.has_data) return 10;
  return Math.max(14, Math.min(100, domain.mastery_score || domain.percent || 0));
};

const IntellectCenter: React.FC<Props> = ({
  onClose,
  onOpenExam,
  onOpenMBTI,
  onOpenMissions,
  onComposeNote,
}) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<IntellectData | null>(null);
  const [customExamOpen, setCustomExamOpen] = useState(false);
  const [examCategories, setExamCategories] = useState<ExamCategory[]>([]);
  const [practicePathways, setPracticePathways] = useState<PracticePathway[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('mixed');
  const [selectedPathway, setSelectedPathway] = useState('');
  const [selectedDomainKey, setSelectedDomainKey] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [mapFlipped, setMapFlipped] = useState(false);

  const load = useCallback(async (quiet = false) => {
    try {
      if (quiet) setRefreshing(true);
      else setLoading(true);
      setError('');
      const res = await gamificationApi.getIntellect();
      setData(res.data);
    } catch (e: any) {
      console.error('加载智维度失败', e);
      setError(e?.response?.data?.detail || e?.message || '智维度数据暂时不可用');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      examApi.getCategories().catch(() => ({ data: [] })),
      examApi.getPracticePathways().catch(() => ({ data: [] })),
    ]).then(([categoryRes, pathwayRes]) => {
      setExamCategories(Array.isArray(categoryRes.data) ? categoryRes.data : []);
      setPracticePathways(Array.isArray(pathwayRes.data) ? pathwayRes.data : []);
    });
  }, []);

  const score = data?.intellect_score ?? data?.intellect ?? 0;
  const examStats = data?.exam_stats;
  const weekly = data?.weekly_tasks;
  const mainAction = useMemo(() => {
    const actions = data?.next_actions || [];
    if (actions.length) return actions[0];
    if (!examStats?.total_attempts) {
      return {
        key: 'exam',
        title: '先完成一次知识考试',
        description: '用一套题建立初始掌握度，后续页面会给出更准确的提升方向。',
        cta: '开始考试',
        target: 'exam',
        gain: 8,
      };
    }
    return {
      key: 'note',
      title: '记录今天的知识收获',
      description: '把一个概念、一段摘录或一个问题写下来，连续学习分会更稳定。',
      cta: '写知识日记',
      target: 'compose_note',
      gain: 2,
    };
  }, [data?.next_actions, examStats?.total_attempts]);

  const runAction = (target: string) => {
    if (target === 'exam') onOpenExam?.();
    else if (target === 'mbti') onOpenMBTI?.();
    else if (target === 'missions') onOpenMissions?.();
    else if (target === 'compose_note') onComposeNote?.();
  };

  const compatiblePathways = practicePathways.filter(pathway =>
    selectedCategory === 'mixed' || pathway.category_keys.includes(selectedCategory)
  );

  useEffect(() => {
    if (selectedPathway && !compatiblePathways.some(pathway => pathway.id === selectedPathway)) {
      setSelectedPathway('');
    }
  }, [selectedPathway, compatiblePathways]);

  const startCustomExam = () => {
    onOpenExam?.({
      category: selectedCategory,
      pathwayId: selectedPathway || undefined,
    });
  };

  const components = data?.components || [];
  const recentExams = data?.recent_exams || [];
  const apiDomains = (data?.top_domains || []).slice(0, 12);
  const categoryDomains: TopDomain[] = examCategories
    .filter(category => category.key !== 'mixed')
    .slice(0, 12)
    .map((category, index) => ({
      key: category.key,
      name: category.name,
      icon: category.icon,
      color: category.color,
      correct: 0,
      total: 0,
      attempts: 0,
      percent: 0,
      mastery_score: 0,
      has_data: false,
      level: '待探索',
      rank: index + 1,
    }));
  const topDomains = apiDomains.length
    ? apiDomains
    : categoryDomains.length
      ? categoryDomains
      : DEFAULT_INTELLECT_DOMAINS;
  const coveredDomains = topDomains.filter(d => d.has_data).length;
  const selectedDomain = topDomains.find(domain => domain.key === selectedDomainKey);
  const radarPoints = topDomains
    .map((domain, idx) => {
      const value = domainValue(domain);
      const p = polarPoint(idx, topDomains.length, 9 + value * 0.36);
      return `${p.x},${p.y}`;
    })
    .join(' ');

  return (
    <div className="intel-root">
      <header className="intel-header">
        <button type="button" className="intel-icon-btn" onClick={onClose} aria-label="返回">
          <BackIcon />
        </button>
        <h1>智 · 知识学习</h1>
        <button type="button" className="intel-icon-btn" onClick={() => load(true)} aria-label="刷新" disabled={refreshing}>
          <RefreshIcon />
        </button>
      </header>

      <main className="intel-main">
        {loading ? (
          <div className="intel-loading">
            <div className="intel-spinner" />
            <p>同步知识成长数据...</p>
          </div>
        ) : error ? (
          <div className="intel-empty full">
            <strong>加载失败</strong>
            <p>{error}</p>
            <button type="button" className="intel-btn primary" onClick={() => load()}>重试</button>
          </div>
        ) : data && (
          <>
            <section className="intel-knowledge-hero">
              <div className="intel-map-head">
                <div>
                  <span className="intel-kicker">知识结构</span>
                  <h2>知识星图</h2>
                  <p>悬浮或轻触图标，探索你的知识领域</p>
                </div>
                <div className="intel-map-actions">
                  <div className="intel-map-status">
                    <span>{coveredDomains}</span>
                    <small>/{topDomains.length || 12} 已点亮</small>
                  </div>
                  <button
                    type="button"
                    className="intel-map-flip-btn"
                    onClick={() => setMapFlipped(value => !value)}
                    aria-pressed={mapFlipped}
                  >
                    {mapFlipped ? '返回星图' : '领域排行'}
                  </button>
                </div>
              </div>

              <div className="intel-map-wrap">
                <div className={`intel-map-flipper ${mapFlipped ? 'flipped' : ''}`}>
                  <div
                    className="intel-radar-card intel-map-face intel-map-front"
                    aria-label={`智维度 ${score} 分，展示知识领域`}
                    aria-hidden={mapFlipped}
                  >
                    <div className="intel-radar-canvas">
                      <svg className="intel-radar-svg" viewBox="0 0 100 100" aria-hidden="true">
                        <defs>
                          <radialGradient id="intel-map-fill" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="#67e8f9" stopOpacity="0.3" />
                            <stop offset="55%" stopColor="#2dd4bf" stopOpacity="0.16" />
                            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.04" />
                          </radialGradient>
                        </defs>
                        {[15, 24, 33, 42].map((r, index) => (
                          <circle
                            key={r}
                            cx="50"
                            cy="50"
                            r={r}
                            className={`intel-radar-ring ring-${index + 1}`}
                          />
                        ))}
                        <circle cx="50" cy="50" r="37.5" className="intel-radar-orbit orbit-one" />
                        <circle cx="50" cy="50" r="28.5" className="intel-radar-orbit orbit-two" />
                        {topDomains.map((domain, idx) => {
                          const end = polarPoint(idx, topDomains.length, 42);
                          const dot = polarPoint(idx, topDomains.length, 9 + domainValue(domain) * 0.36);
                          return (
                            <g key={domain.key}>
                              <line x1="50" y1="50" x2={end.x} y2={end.y} className="intel-radar-axis" />
                              <circle
                                cx={dot.x}
                                cy={dot.y}
                                r={domain.has_data ? 1.55 : 1.1}
                                fill={domain.has_data ? domain.color : 'rgba(148,163,184,0.8)'}
                              />
                            </g>
                          );
                        })}
                        {radarPoints && <polygon points={radarPoints} className="intel-radar-shape" />}
                      </svg>

                      <div className="intel-radar-core">
                        <span>智</span>
                        <strong>{score}</strong>
                      </div>

                      {topDomains.map((domain, idx) => {
                        const p = polarPoint(idx, topDomains.length, 43);
                        const selected = selectedDomainKey === domain.key;
                        return (
                          <button
                            type="button"
                            key={domain.key}
                            className={`intel-domain-node ${domain.has_data ? 'learned' : 'empty'} ${selected ? 'selected' : ''} ${p.y < 24 ? 'tip-below' : ''}`}
                            style={{
                              '--x': `${p.x}%`,
                              '--y': `${p.y}%`,
                              '--domain-color': domain.color,
                            } as React.CSSProperties}
                            onClick={() => setSelectedDomainKey(selected ? '' : domain.key)}
                            aria-label={domain.name}
                            aria-pressed={selected}
                          >
                            <span className="intel-domain-icon" aria-hidden="true">{domain.icon}</span>
                            <span className="intel-domain-tooltip">
                              <strong>{domain.name}</strong>
                              <small>{domain.has_data ? domain.level : '待探索'}</small>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="intel-radar-caption">
                      <span>{selectedDomain ? `已选择 · ${selectedDomain.name}` : '轻触领域查看名称'}</span>
                      <button type="button" onClick={() => onOpenExam?.()}>进入知识考试</button>
                    </div>
                  </div>

                  <div
                    className="intel-radar-card intel-map-face intel-map-back"
                    aria-label="知识领域掌握度排行"
                    aria-hidden={!mapFlipped}
                  >
                    <div className="intel-ranking-head">
                      <div>
                        <span>能力画像</span>
                        <h3>擅长领域排行</h3>
                        <p>基于正确率与答题样本量综合计算</p>
                      </div>
                      <strong>智 {score}</strong>
                    </div>
                    <div className="intel-ranking-list">
                      {topDomains.map((domain, index) => {
                        const mastery = clampPct(domain.mastery_score || domain.percent);
                        return (
                          <div className="intel-ranking-row" key={domain.key}>
                            <span className={`intel-ranking-no ${index < 3 ? 'top' : ''}`}>{index + 1}</span>
                            <span className="intel-ranking-icon" style={{ '--domain-color': domain.color } as React.CSSProperties}>
                              {domain.icon}
                            </span>
                            <div className="intel-ranking-copy">
                              <div>
                                <strong>{domain.name}</strong>
                                <span>{domain.has_data ? domain.level : '待探索'}</span>
                              </div>
                              <div className="intel-ranking-track">
                                <i style={{ width: `${mastery}%`, background: domain.color }} />
                              </div>
                            </div>
                            <b>{mastery}%</b>
                          </div>
                        );
                      })}
                    </div>
                    <button type="button" className="intel-ranking-return" onClick={() => setMapFlipped(false)}>
                      翻回知识星图
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="intel-next">
              <div>
                <span className="intel-section-eyebrow">下一步</span>
                <h2>{mainAction.title}</h2>
                <p>{mainAction.description}</p>
              </div>
              <button type="button" className="intel-btn primary" onClick={() => runAction(mainAction.target)}>
                {mainAction.cta}
              </button>
            </section>

            <section className={`intel-custom-exam ${customExamOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="intel-custom-toggle"
                onClick={() => setCustomExamOpen(value => !value)}
                aria-expanded={customExamOpen}
              >
                <span>
                  <strong>自定义答题</strong>
                  <small>{customExamOpen ? '选择领域与修炼途径' : '按领域或途径专项练习'}</small>
                </span>
                <b>{customExamOpen ? '收起' : '设置'}</b>
              </button>

              {customExamOpen && (
                <div className="intel-custom-body">
                  <div className="intel-custom-group">
                    <span>答题领域</span>
                    <div className="intel-custom-options">
                      {examCategories.map(category => (
                        <button
                          key={category.key}
                          type="button"
                          className={selectedCategory === category.key ? 'selected' : ''}
                          style={{ '--option-color': category.color } as React.CSSProperties}
                          onClick={() => setSelectedCategory(category.key)}
                        >
                          <i>{category.icon}</i>
                          <b>{category.name}</b>
                        </button>
                      ))}
                    </div>
                  </div>

                  {compatiblePathways.length > 0 && (
                    <div className="intel-custom-group">
                      <span>修炼途径 <em>可选</em></span>
                      <div className="intel-pathway-options">
                        {compatiblePathways.map(pathway => (
                          <button
                            key={pathway.id}
                            type="button"
                            className={selectedPathway === pathway.id ? 'selected' : ''}
                            style={{ '--option-color': pathway.color } as React.CSSProperties}
                            onClick={() => setSelectedPathway(
                              selectedPathway === pathway.id ? '' : pathway.id
                            )}
                          >
                            <i>{pathway.icon}</i>
                            <span>
                              <b>{pathway.name}</b>
                              <small>第 {pathway.current_sequence} 级 · {pathway.question_count} 题</small>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <button type="button" className="intel-custom-start" onClick={startCustomExam}>
                    开始自定义答题
                  </button>
                </div>
              )}
            </section>

            <section className={`intel-archive ${detailsOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="intel-archive-toggle"
                onClick={() => setDetailsOpen(value => !value)}
                aria-expanded={detailsOpen}
              >
                <span>
                  <strong>成长档案</strong>
                  <small>分数构成、认知画像与考试记录</small>
                </span>
                <b>{detailsOpen ? '收起' : '查看'}</b>
              </button>

              {detailsOpen && (
                <div className="intel-archive-body">
                  <div className="intel-stats-grid">
                    <div>
                      <span>考试次数</span>
                      <strong>{examStats?.total_attempts ?? 0}</strong>
                    </div>
                    <div>
                      <span>累计答题</span>
                      <strong>{examStats?.total_answered ?? 0}</strong>
                    </div>
                    <div>
                      <span>连续学习</span>
                      <strong>{examStats?.study_streak ?? 0} 天</strong>
                    </div>
                    <div>
                      <span>本周任务</span>
                      <strong>{weekly?.total ? `${weekly.completed}/${weekly.total}` : '未生成'}</strong>
                    </div>
                  </div>

                  {components.length > 0 && (
                    <div className="intel-archive-block">
                      <div className="intel-section-head compact">
                        <div>
                          <span className="intel-section-eyebrow">分数来源</span>
                          <h2>成分分解</h2>
                        </div>
                      </div>
                      <div className="intel-component-list">
                        {components.map(c => (
                          <div key={c.key} className="intel-component">
                            <div className="intel-component-top">
                              <div>
                                <strong>{c.label}</strong>
                                <p>{c.description}</p>
                              </div>
                              <span>{c.value}/{c.max}</span>
                            </div>
                            <div className="intel-track">
                              <div className="intel-fill" style={{ width: `${clampPct(c.pct)}%`, background: c.color }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="intel-two-col">
                    <div className="intel-panel">
                      <div className="intel-section-head compact">
                        <div>
                          <span className="intel-section-eyebrow">认知画像</span>
                          <h2>MBTI</h2>
                        </div>
                        {data.latest_mbti?.date_label && <span>{data.latest_mbti.date_label}</span>}
                      </div>
                      {data.latest_mbti ? (
                        <div className="intel-mbti">
                          <div className="intel-mbti-type">{data.latest_mbti.type}</div>
                          {data.latest_mbti.description && <p>{data.latest_mbti.description}</p>}
                          <button type="button" className="intel-btn subtle" onClick={onOpenMBTI}>重新测试</button>
                        </div>
                      ) : (
                        <div className="intel-empty">
                          <strong>尚未完成自测</strong>
                          <p>补齐认知画像，让成长建议更贴合你。</p>
                          <button type="button" className="intel-btn subtle" onClick={onOpenMBTI}>开始测试</button>
                        </div>
                      )}
                    </div>

                    <div className="intel-panel">
                      <div className="intel-section-head compact">
                        <div>
                          <span className="intel-section-eyebrow">最近表现</span>
                          <h2>考试记录</h2>
                        </div>
                      </div>
                      {recentExams.length ? (
                        <div className="intel-exam-list">
                          {recentExams.slice(0, 3).map(exam => (
                            <div key={exam.id} className="intel-exam-row">
                              <div className="intel-exam-left">
                                <span>{exam.category_icon}</span>
                                <div>
                                  <strong>{exam.category_name}</strong>
                                  <small>{exam.created_at?.slice(0, 16).replace('T', ' ')}</small>
                                </div>
                              </div>
                              <div className={`intel-exam-score ${exam.score_percent >= 80 ? 'good' : exam.score_percent >= 60 ? 'mid' : 'low'}`}>
                                {exam.score_percent}%
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="intel-empty">
                          <strong>还没有考试记录</strong>
                          <p>完成一套题后，这里会沉淀你的表现。</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>

            <div className="intel-bottom-actions">
              <button type="button" className="intel-btn primary" onClick={() => onOpenExam?.()}>开始答题</button>
              <button type="button" className="intel-btn subtle" onClick={onComposeNote}>写知识日记</button>
              <button type="button" className="intel-btn subtle" onClick={onOpenMissions}>周任务</button>
            </div>
          </>
        )}
      </main>

      <style>{intelStyles}</style>
    </div>
  );
};

const intelStyles = `
.intel-root { min-height: 100vh; background: #030712; color: #f8fafc; font-family: inherit; -webkit-tap-highlight-color: transparent; }
.intel-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; padding-top: calc(16px + var(--status-bar-height, 28px)); background: rgba(3,7,18,0.94); border-bottom: 1px solid rgba(20,184,166,0.16); position: sticky; top: 0; z-index: 10; backdrop-filter: blur(14px); }
.intel-header h1 { font-size: 17px; margin: 0; font-weight: 800; }
.intel-icon-btn { width: 36px; height: 36px; border-radius: 10px; border: 1px solid rgba(45,212,191,0.22); background: rgba(13,148,136,0.12); color: #ccfbf1; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.intel-icon-btn:disabled { opacity: 0.5; cursor: default; }
.intel-main { width: min(1060px, 100%); margin: 0 auto; padding: 16px; padding-bottom: 104px; }
.intel-loading { text-align: center; padding: 54px 16px; color: #94a3b8; }
.intel-spinner { width: 32px; height: 32px; border: 3px solid rgba(255,255,255,0.1); border-top-color: #14b8a6; border-radius: 50%; animation: intel-spin 0.8s linear infinite; margin: 0 auto 12px; }
@keyframes intel-spin { to { transform: rotate(360deg); } }
.intel-knowledge-hero { position: relative; isolation: isolate; overflow: hidden; padding: 20px; border-radius: 24px; background: radial-gradient(circle at 18% 5%, rgba(34,211,238,0.16), transparent 34%), radial-gradient(circle at 86% 90%, rgba(59,130,246,0.16), transparent 34%), linear-gradient(145deg, rgba(8,47,73,0.76), rgba(7,15,31,0.96) 48%, rgba(14,28,54,0.92)); border: 1px solid rgba(103,232,249,0.18); box-shadow: 0 24px 80px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04); }
.intel-knowledge-hero::before { content: ""; position: absolute; z-index: -1; width: 360px; height: 360px; left: 50%; top: 54%; transform: translate(-50%, -50%); border-radius: 50%; background: rgba(45,212,191,0.08); filter: blur(72px); pointer-events: none; }
.intel-map-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 4px; }
.intel-map-head h2 { margin: 0; color: #f8fafc; font-size: 18px; font-weight: 900; }
.intel-map-head p { margin: 5px 0 0; color: #6f91a8; font-size: 11px; }
.intel-map-actions { display: flex; align-items: center; gap: 8px; }
.intel-map-status { flex: 0 0 auto; display: flex; align-items: baseline; gap: 3px; padding: 7px 10px; border-radius: 999px; background: rgba(8,47,73,0.52); border: 1px solid rgba(103,232,249,0.12); }
.intel-map-status span { color: #a5f3fc; font-size: 16px; line-height: 1; font-weight: 950; }
.intel-map-status small { color: #5f8096; font-size: 10px; font-weight: 700; }
.intel-map-flip-btn { min-height: 32px; padding: 0 11px; border-radius: 999px; border: 1px solid rgba(103,232,249,0.2); background: rgba(14,116,144,0.18); color: #a5f3fc; font-family: inherit; font-size: 10px; font-weight: 850; cursor: pointer; }
.intel-map-wrap { display: block; }
.intel-map-flipper { display: grid; perspective: 1500px; transform-style: preserve-3d; }
.intel-map-face { grid-area: 1 / 1; min-width: 0; backface-visibility: hidden; transition: transform 620ms cubic-bezier(.2,.72,.2,1), opacity 300ms ease; }
.intel-map-front { transform: rotateY(0deg); }
.intel-map-back { transform: rotateY(180deg); pointer-events: none; opacity: 0; }
.intel-map-flipper.flipped .intel-map-front { transform: rotateY(-180deg); pointer-events: none; opacity: 0; }
.intel-map-flipper.flipped .intel-map-back { transform: rotateY(0deg); pointer-events: auto; opacity: 1; }
.intel-radar-card { position: relative; border: 1px solid rgba(103,232,249,0.1); border-radius: 20px; background: radial-gradient(circle at 50% 48%, rgba(13,148,136,0.14), rgba(2,6,23,0.24) 56%, rgba(2,6,23,0.5)); color: inherit; padding: 10px 14px 12px; overflow: hidden; }
.intel-radar-card::before { content: ""; position: absolute; inset: 0; opacity: 0.32; background-image: radial-gradient(rgba(165,243,252,0.42) 0.65px, transparent 0.8px); background-size: 28px 28px; mask-image: radial-gradient(circle at center, black, transparent 78%); pointer-events: none; }
.intel-radar-canvas { position: relative; width: min(680px, 100%); aspect-ratio: 1 / 0.67; min-height: 360px; margin: 0 auto; }
.intel-radar-svg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
.intel-radar-ring { fill: none; stroke: rgba(103,232,249,0.1); stroke-width: 0.42; }
.intel-radar-axis { stroke: rgba(125,211,252,0.1); stroke-width: 0.32; stroke-dasharray: 1.2 1.8; }
.intel-radar-orbit { fill: none; stroke: rgba(94,234,212,0.24); stroke-width: 0.45; stroke-linecap: round; stroke-dasharray: 2 8; transform-origin: 50px 50px; animation: intel-orbit 24s linear infinite; }
.intel-radar-orbit.orbit-two { stroke: rgba(96,165,250,0.2); stroke-dasharray: 1 6; animation-direction: reverse; animation-duration: 18s; }
.intel-radar-shape { fill: url(#intel-map-fill); stroke: rgba(103,232,249,0.76); stroke-width: 0.75; filter: drop-shadow(0 0 10px rgba(34,211,238,0.28)); transition: points 0.5s ease; }
.intel-radar-core { position: absolute; left: 50%; top: 50%; width: 118px; height: 118px; transform: translate(-50%, -50%); border-radius: 50%; background: radial-gradient(circle at 38% 28%, rgba(207,250,254,0.22), rgba(13,148,136,0.3) 44%, rgba(6,19,38,0.98) 74%); border: 1px solid rgba(103,232,249,0.34); box-shadow: 0 0 0 8px rgba(45,212,191,0.035), 0 0 44px rgba(34,211,238,0.2), inset 0 0 24px rgba(103,232,249,0.08); display: flex; flex-direction: column; align-items: center; justify-content: center; animation: intel-core-pulse 4.5s ease-in-out infinite; }
.intel-radar-core::before, .intel-radar-core::after { content: ""; position: absolute; border-radius: 50%; border: 1px solid rgba(103,232,249,0.12); pointer-events: none; }
.intel-radar-core::before { inset: -13px; }
.intel-radar-core::after { inset: -25px; border-style: dashed; animation: intel-orbit 18s linear infinite; }
.intel-radar-core span { color: #67e8f9; font-size: 15px; line-height: 1; font-weight: 950; text-shadow: 0 0 16px rgba(103,232,249,0.54); }
.intel-radar-core strong { color: #f8fafc; font-size: 44px; line-height: 1; font-weight: 950; margin-top: 3px; }
.intel-domain-node { position: absolute; z-index: 3; left: var(--x); top: var(--y); width: 48px; min-width: 48px; max-width: 48px; height: 48px; min-height: 48px; max-height: 48px; aspect-ratio: 1; box-sizing: border-box; appearance: none; transform: translate(-50%, -50%); display: grid; place-items: center; padding: 0; line-height: 1; border-radius: 999px; border: 1px solid color-mix(in srgb, var(--domain-color) 48%, rgba(255,255,255,0.08)); background: color-mix(in srgb, var(--domain-color) 16%, rgba(5,14,30,0.94)); box-shadow: 0 10px 28px rgba(0,0,0,0.25), 0 0 20px color-mix(in srgb, var(--domain-color) 20%, transparent); color: #fff; cursor: pointer; font-family: inherit; transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, opacity 180ms ease; }
.intel-domain-node::after { content: ""; position: absolute; inset: -5px; border-radius: 50%; border: 1px solid color-mix(in srgb, var(--domain-color) 22%, transparent); opacity: 0; transform: scale(0.82); transition: opacity 180ms ease, transform 180ms ease; }
.intel-domain-node.empty { opacity: 0.62; filter: saturate(0.58); }
.intel-domain-node:hover, .intel-domain-node:focus-visible, .intel-domain-node.selected { z-index: 6; opacity: 1; transform: translate(-50%, -50%) scale(1.13); outline: none; border-color: color-mix(in srgb, var(--domain-color) 78%, white); box-shadow: 0 14px 34px rgba(0,0,0,0.3), 0 0 30px color-mix(in srgb, var(--domain-color) 42%, transparent); }
.intel-domain-node:hover::after, .intel-domain-node:focus-visible::after, .intel-domain-node.selected::after { opacity: 1; transform: scale(1); }
.intel-domain-icon { font-size: 21px; line-height: 1; filter: drop-shadow(0 2px 5px rgba(0,0,0,0.3)); }
.intel-domain-tooltip { position: absolute; left: 50%; bottom: calc(100% + 10px); min-width: max-content; max-width: 130px; transform: translate(-50%, 5px) scale(0.96); padding: 7px 10px; border-radius: 10px; border: 1px solid color-mix(in srgb, var(--domain-color) 35%, rgba(255,255,255,0.08)); background: rgba(3,10,24,0.94); box-shadow: 0 12px 30px rgba(0,0,0,0.34); opacity: 0; visibility: hidden; pointer-events: none; transition: opacity 150ms ease, transform 150ms ease, visibility 150ms ease; }
.intel-domain-node.tip-below .intel-domain-tooltip { top: calc(100% + 10px); bottom: auto; }
.intel-domain-tooltip strong { display: block; color: #f8fafc; font-size: 11px; white-space: nowrap; }
.intel-domain-tooltip small { display: block; margin-top: 2px; color: color-mix(in srgb, var(--domain-color) 72%, #cbd5e1); font-size: 9px; font-weight: 700; white-space: nowrap; }
.intel-domain-node:hover .intel-domain-tooltip, .intel-domain-node:focus-visible .intel-domain-tooltip, .intel-domain-node.selected .intel-domain-tooltip { opacity: 1; visibility: visible; transform: translate(-50%, 0) scale(1); }
.intel-radar-caption { position: relative; z-index: 4; display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: -2px; color: #5f8096; font-size: 11px; }
.intel-radar-caption button { border: 0; background: transparent; color: #67e8f9; font-family: inherit; font-size: 11px; font-weight: 850; cursor: pointer; }
.intel-map-back { padding: 18px; background: radial-gradient(circle at 90% 0%, rgba(59,130,246,0.18), transparent 36%), linear-gradient(155deg, rgba(8,47,73,0.72), rgba(2,6,23,0.94)); }
.intel-map-back::before { opacity: 0.2; }
.intel-ranking-head { position: relative; z-index: 1; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.intel-ranking-head span { color: #5eead4; font-size: 10px; font-weight: 850; letter-spacing: .08em; }
.intel-ranking-head h3 { margin: 4px 0 0; color: #f8fafc; font-size: 18px; }
.intel-ranking-head p { margin: 5px 0 0; color: #64748b; font-size: 10px; }
.intel-ranking-head > strong { flex: 0 0 auto; color: #a5f3fc; font-size: 15px; padding: 7px 10px; border-radius: 999px; border: 1px solid rgba(103,232,249,0.15); background: rgba(8,47,73,0.5); }
.intel-ranking-list { position: relative; z-index: 1; display: flex; flex-direction: column; gap: 6px; }
.intel-ranking-row { min-height: 30px; display: grid; grid-template-columns: 24px 32px minmax(0,1fr) 40px; align-items: center; gap: 9px; padding: 5px 8px; border-radius: 10px; background: rgba(15,23,42,0.56); border: 1px solid rgba(148,163,184,0.07); }
.intel-ranking-no { color: #64748b; font-size: 10px; font-weight: 900; text-align: center; }
.intel-ranking-no.top { color: #fbbf24; }
.intel-ranking-icon { width: 30px; height: 30px; border-radius: 50%; display: grid; place-items: center; background: color-mix(in srgb, var(--domain-color) 15%, rgba(2,6,23,.85)); border: 1px solid color-mix(in srgb, var(--domain-color) 32%, transparent); font-size: 14px; }
.intel-ranking-copy { min-width: 0; }
.intel-ranking-copy > div:first-child { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
.intel-ranking-copy strong { color: #dbeafe; font-size: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.intel-ranking-copy span { color: #64748b; font-size: 9px; white-space: nowrap; }
.intel-ranking-track { height: 4px; overflow: hidden; border-radius: 999px; background: rgba(30,41,59,.9); }
.intel-ranking-track i { display: block; height: 100%; min-width: 2px; border-radius: inherit; box-shadow: 0 0 12px currentColor; transition: width 700ms ease; }
.intel-ranking-row > b { color: #ccfbf1; font-size: 11px; text-align: right; }
.intel-ranking-return { position: relative; z-index: 1; width: 100%; min-height: 34px; margin-top: 10px; border-radius: 10px; border: 1px solid rgba(103,232,249,0.14); background: rgba(14,116,144,0.12); color: #67e8f9; font-family: inherit; font-size: 11px; font-weight: 850; cursor: pointer; }
@keyframes intel-orbit { to { transform: rotate(360deg); } }
@keyframes intel-core-pulse { 0%, 100% { box-shadow: 0 0 0 8px rgba(45,212,191,0.035), 0 0 36px rgba(34,211,238,0.16), inset 0 0 24px rgba(103,232,249,0.08); } 50% { box-shadow: 0 0 0 12px rgba(45,212,191,0.045), 0 0 58px rgba(34,211,238,0.27), inset 0 0 30px rgba(103,232,249,0.12); } }
.intel-map-insight { border-radius: 16px; background: rgba(2,6,23,0.34); border: 1px solid rgba(148,163,184,0.08); padding: 15px; }
.intel-map-insight > span { display: block; color: #5eead4; font-size: 11px; font-weight: 800; margin-bottom: 8px; }
.intel-map-insight > strong { display: block; color: #f1f5f9; font-size: 18px; line-height: 1.25; margin-bottom: 8px; }
.intel-map-insight p { margin: 0; color: #94a3b8; font-size: 12px; line-height: 1.55; }
.intel-subdomain-line { margin-top: 9px; color: #7dd3fc; font-size: 11px; line-height: 1.45; }
.intel-domain-rank { display: flex; flex-direction: column; gap: 8px; margin-top: 14px; }
.intel-domain-rank div { display: grid; grid-template-columns: 22px minmax(0, 1fr) auto; align-items: center; gap: 8px; border-radius: 10px; background: rgba(15,23,42,0.64); padding: 9px 10px; }
.intel-domain-rank span { width: 22px; height: 22px; border-radius: 50%; display: grid; place-items: center; background: rgba(2,6,23,0.55); font-size: 11px; font-weight: 950; }
.intel-domain-rank strong { color: #e2e8f0; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.intel-domain-rank em { color: #ccfbf1; font-size: 13px; font-style: normal; font-weight: 950; }
.intel-map-mini { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-top: 14px; }
.intel-map-mini div { border-radius: 10px; background: rgba(15,23,42,0.72); padding: 10px 6px; text-align: center; }
.intel-map-mini strong { display: block; color: #ccfbf1; font-size: 18px; }
.intel-map-mini span { display: block; color: #64748b; font-size: 10px; margin-top: 2px; }
.intel-hero { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 18px; align-items: center; padding: 22px; border-radius: 20px; background: linear-gradient(145deg, rgba(20,184,166,0.18), rgba(15,23,42,0.88) 56%, rgba(59,130,246,0.12)); border: 1px solid rgba(94,234,212,0.16); box-shadow: 0 16px 48px rgba(0,0,0,0.18); }
.intel-kicker, .intel-section-eyebrow { display: block; color: #5eead4; font-size: 11px; font-weight: 800; margin-bottom: 6px; }
.intel-score-row { display: flex; align-items: baseline; gap: 4px; line-height: 1; }
.intel-score-row strong { font-size: 58px; font-weight: 950; color: #ccfbf1; }
.intel-score-row small { color: #5eead4; font-size: 18px; font-weight: 800; }
.intel-hero p { margin: 12px 0 0; color: #94a3b8; font-size: 13px; line-height: 1.55; max-width: 560px; }
.intel-hero-meter { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #94a3b8; font-size: 11px; }
.intel-meter-ring { --score: 0%; width: 108px; height: 108px; border-radius: 50%; display: grid; place-items: center; background: conic-gradient(#14b8a6 var(--score), rgba(51,65,85,0.76) 0); position: relative; }
.intel-meter-ring::after { content: ""; position: absolute; inset: 9px; border-radius: 50%; background: #07111f; border: 1px solid rgba(148,163,184,0.12); }
.intel-meter-ring span { position: relative; z-index: 1; font-size: 26px; font-weight: 950; color: #f8fafc; }
.intel-next { margin-top: 14px; display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 16px; align-items: center; padding: 16px; border-radius: 16px; background: rgba(15,23,42,0.76); border: 1px solid rgba(45,212,191,0.13); }
.intel-next h2, .intel-section h2, .intel-panel h2 { margin: 0; font-size: 16px; color: #f1f5f9; }
.intel-next p { margin: 6px 0 0; color: #94a3b8; font-size: 13px; line-height: 1.45; }
.intel-custom-exam { margin-top: 8px; border-top: 1px solid rgba(148,163,184,0.06); }
.intel-custom-toggle { width: 100%; min-height: 42px; padding: 8px 4px; border: 0; background: transparent; color: inherit; display: flex; align-items: center; justify-content: space-between; gap: 12px; text-align: left; cursor: pointer; font-family: inherit; }
.intel-custom-toggle span { min-width: 0; }
.intel-custom-toggle strong { display: block; color: #94a3b8; font-size: 12px; font-weight: 750; }
.intel-custom-toggle small { display: block; color: #475569; font-size: 10px; margin-top: 2px; }
.intel-custom-toggle > b { color: #64748b; font-size: 11px; font-weight: 700; }
.intel-custom-exam.open { margin-top: 12px; border: 1px solid rgba(45,212,191,0.12); border-radius: 14px; background: rgba(15,23,42,0.44); padding: 0 12px 12px; }
.intel-custom-exam.open .intel-custom-toggle { border-bottom: 1px solid rgba(148,163,184,0.07); margin-bottom: 12px; }
.intel-custom-body { display: flex; flex-direction: column; gap: 14px; }
.intel-custom-group > span { display: block; color: #94a3b8; font-size: 11px; font-weight: 800; margin-bottom: 8px; }
.intel-custom-group > span em { color: #475569; font-style: normal; font-weight: 600; margin-left: 4px; }
.intel-custom-options { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 3px; scrollbar-width: thin; }
.intel-custom-options button { flex: 0 0 auto; min-height: 34px; display: flex; align-items: center; gap: 6px; padding: 0 10px; border-radius: 8px; border: 1px solid rgba(148,163,184,0.1); background: rgba(2,6,23,0.32); color: #94a3b8; font-family: inherit; cursor: pointer; }
.intel-custom-options button.selected { color: #f8fafc; border-color: var(--option-color, #14b8a6); background: color-mix(in srgb, var(--option-color) 12%, rgba(2,6,23,0.6)); }
.intel-custom-options i { font-style: normal; font-size: 14px; }
.intel-custom-options b { font-size: 11px; white-space: nowrap; }
.intel-pathway-options { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; }
.intel-pathway-options button { min-width: 0; display: flex; align-items: center; gap: 8px; padding: 9px; border-radius: 9px; border: 1px solid rgba(148,163,184,0.09); background: rgba(2,6,23,0.3); color: inherit; text-align: left; font-family: inherit; cursor: pointer; }
.intel-pathway-options button.selected { border-color: var(--option-color, #14b8a6); background: color-mix(in srgb, var(--option-color) 10%, rgba(2,6,23,0.54)); }
.intel-pathway-options i { flex: 0 0 26px; font-size: 18px; font-style: normal; text-align: center; }
.intel-pathway-options span { min-width: 0; }
.intel-pathway-options b { display: block; color: #cbd5e1; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.intel-pathway-options small { display: block; color: #475569; font-size: 9px; margin-top: 2px; }
.intel-custom-start { align-self: flex-end; min-height: 36px; padding: 0 14px; border: 0; border-radius: 9px; background: #0f766e; color: #f0fdfa; font-family: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
.intel-archive { margin-top: 10px; border-top: 1px solid rgba(148,163,184,0.07); }
.intel-archive.open { margin-top: 14px; border: 1px solid rgba(103,232,249,0.1); border-radius: 16px; background: rgba(8,15,30,0.5); padding: 0 14px 14px; }
.intel-archive-toggle { width: 100%; min-height: 48px; padding: 9px 4px; border: 0; background: transparent; color: inherit; display: flex; align-items: center; justify-content: space-between; gap: 12px; text-align: left; cursor: pointer; font-family: inherit; }
.intel-archive.open .intel-archive-toggle { border-bottom: 1px solid rgba(148,163,184,0.07); margin-bottom: 14px; }
.intel-archive-toggle strong { display: block; color: #94a3b8; font-size: 12px; }
.intel-archive-toggle small { display: block; color: #475569; font-size: 10px; margin-top: 2px; }
.intel-archive-toggle b { color: #67e8f9; font-size: 11px; }
.intel-archive-body { animation: intel-archive-in 220ms ease both; }
.intel-archive-block { margin-top: 16px; }
@keyframes intel-archive-in { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
.intel-btn { border: none; border-radius: 12px; min-height: 40px; padding: 0 14px; font-weight: 800; font-family: inherit; cursor: pointer; color: #fff; white-space: nowrap; }
.intel-btn.primary { background: linear-gradient(135deg, #0d9488, #2563eb); box-shadow: 0 10px 28px rgba(20,184,166,0.16); }
.intel-btn.subtle { background: rgba(15,23,42,0.86); color: #bae6fd; border: 1px solid rgba(125,211,252,0.16); }
.intel-section, .intel-panel { margin-top: 16px; }
.intel-section-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.intel-section-head.compact { margin-bottom: 12px; align-items: flex-start; }
.intel-section-head span { color: #64748b; font-size: 11px; font-weight: 700; }
.intel-component-list { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; }
.intel-component, .intel-panel, .intel-stats-grid > div, .intel-exam-row, .intel-empty { background: rgba(15,23,42,0.72); border: 1px solid rgba(148,163,184,0.08); border-radius: 14px; }
.intel-component { padding: 12px; min-height: 128px; display: flex; flex-direction: column; justify-content: space-between; }
.intel-component-top { display: flex; justify-content: space-between; gap: 10px; }
.intel-component strong { display: block; color: #e2e8f0; font-size: 12px; }
.intel-component p { margin: 5px 0 0; color: #64748b; font-size: 11px; line-height: 1.45; }
.intel-component-top span { color: #cbd5e1; font-size: 12px; font-weight: 900; white-space: nowrap; }
.intel-track { height: 6px; border-radius: 999px; background: rgba(30,41,59,0.9); overflow: hidden; margin-top: 12px; }
.intel-fill { height: 100%; border-radius: 999px; transition: width 0.6s ease; }
.intel-stats-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.intel-stats-grid > div { padding: 14px; }
.intel-stats-grid span { display: block; color: #64748b; font-size: 11px; margin-bottom: 7px; }
.intel-stats-grid strong { font-size: 20px; color: #ccfbf1; }
.intel-two-col { display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr); gap: 12px; margin-top: 16px; }
.intel-panel { padding: 14px; margin-top: 0; }
.intel-action { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px; text-align: left; padding: 12px; margin-bottom: 8px; border-radius: 12px; border: 1px solid rgba(20,184,166,0.12); background: rgba(2,6,23,0.36); color: inherit; cursor: pointer; font-family: inherit; }
.intel-action strong { display: block; color: #e2e8f0; font-size: 13px; margin-bottom: 4px; }
.intel-action small { display: block; color: #94a3b8; font-size: 11px; line-height: 1.45; }
.intel-action em { flex: 0 0 auto; color: #5eead4; font-size: 12px; font-style: normal; font-weight: 800; }
.intel-mbti { text-align: center; }
.intel-mbti-type { width: max-content; margin: 4px auto 10px; padding: 8px 20px; border-radius: 12px; background: rgba(20,184,166,0.13); border: 1px solid rgba(94,234,212,0.24); color: #99f6e4; font-size: 26px; font-weight: 950; letter-spacing: 0; }
.intel-mbti p { margin: 0 0 12px; color: #94a3b8; font-size: 12px; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
.intel-empty { padding: 14px; color: #94a3b8; font-size: 13px; }
.intel-empty.full { margin-top: 24px; text-align: center; }
.intel-empty strong { display: block; color: #e2e8f0; margin-bottom: 5px; }
.intel-empty p { margin: 0 0 12px; line-height: 1.5; }
.intel-link-btn { border: none; background: transparent; color: #5eead4; font-weight: 800; font-family: inherit; cursor: pointer; }
.intel-exam-list { display: flex; flex-direction: column; gap: 8px; }
.intel-exam-row { display: flex; align-items: center; justify-content: space-between; padding: 12px; }
.intel-exam-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.intel-exam-left > span { width: 32px; height: 32px; border-radius: 10px; display: grid; place-items: center; background: rgba(20,184,166,0.1); }
.intel-exam-left strong { display: block; color: #e2e8f0; font-size: 13px; }
.intel-exam-left small { display: block; color: #64748b; font-size: 11px; margin-top: 2px; }
.intel-exam-score { font-weight: 950; font-size: 18px; }
.intel-exam-score.good { color: #34d399; }
.intel-exam-score.mid { color: #facc15; }
.intel-exam-score.low { color: #f87171; }
.intel-bottom-actions { position: fixed; left: 0; right: 0; bottom: 0; z-index: 20; display: flex; gap: 8px; padding: 12px 16px; padding-bottom: calc(12px + var(--safe-area-inset-bottom, 0px)); background: linear-gradient(180deg, rgba(3,7,18,0), rgba(3,7,18,0.96) 22%); }
.intel-bottom-actions .intel-btn { flex: 1; }
@media (max-width: 920px) {
  .intel-component-list { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .intel-two-col { grid-template-columns: 1fr; }
}
@media (max-width: 620px) {
  .intel-main { padding: 12px; padding-bottom: 112px; }
  .intel-knowledge-hero { padding: 14px; }
  .intel-map-head h2 { font-size: 16px; }
  .intel-map-actions { flex-direction: column; align-items: flex-end; gap: 5px; }
  .intel-map-flip-btn { min-height: 28px; }
  .intel-radar-card { padding: 10px; }
  .intel-radar-canvas { aspect-ratio: 1 / 0.94; min-height: 326px; }
  .intel-radar-core { width: 92px; height: 92px; }
  .intel-radar-core strong { font-size: 32px; }
  .intel-radar-core span { font-size: 13px; }
  .intel-domain-node { width: 39px; min-width: 39px; max-width: 39px; height: 39px; min-height: 39px; max-height: 39px; }
  .intel-domain-icon { font-size: 18px; }
  .intel-domain-tooltip { padding: 6px 8px; }
  .intel-map-back { padding: 13px 10px; }
  .intel-ranking-row { grid-template-columns: 20px 30px minmax(0,1fr) 36px; gap: 7px; padding: 4px 6px; }
  .intel-hero { grid-template-columns: 1fr; padding: 18px; }
  .intel-hero-meter { align-items: flex-start; }
  .intel-next { grid-template-columns: 1fr; }
  .intel-next .intel-btn { width: 100%; }
  .intel-pathway-options { grid-template-columns: 1fr; }
  .intel-stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .intel-component-list { grid-template-columns: 1fr; }
  .intel-bottom-actions { flex-wrap: wrap; }
  .intel-score-row strong { font-size: 50px; }
}
@media (prefers-reduced-motion: reduce) {
  .intel-radar-orbit, .intel-radar-core, .intel-radar-core::after { animation: none; }
}
`;

export default IntellectCenter;
